import React, { useState, useEffect, useCallback } from "react";
import Header from "../components/Header";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import EngagementTable from "../components/Dashboardviz/D09/D09Viz01";
import { useFilter } from "../FilterContext";
import jsonData from "../current-survey-numeric.json"; // Demographic categories
import questionData from "../mq-list-2025.json"; // Questions data
import { FILTER_CONFIG } from "../config/filterconfig";
import "./D09_Engagement_Questions_Sorted.css";

const CCISortedEngagementQuestions = () => {
  const { filterCriteria, setFilterCriteria } = useFilter();
  const [data, setData] = useState([]);
  const [filterOptions, setFilterOptions] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState("asc");

  // Add refresh page functionality
  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  // Generate dynamic filter options from demographic data
  useEffect(() => {
    const generateFilterOptions = () => {
      const options = {};
      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(jsonData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter((value) => value !== null);

        // Apply custom sorting if defined
        if (sortOrder) {
          uniqueValues.sort(
            (a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b)
          );
        } else {
          uniqueValues.sort((a, b) =>
            a?.localeCompare(b, undefined, { sensitivity: "base" })
          );
        }

        options[fieldName] = uniqueValues;
      });
      setFilterOptions(options);
    };

    generateFilterOptions();
  }, []);

  // Process engagement question data by mapping responses and calculating averages
  useEffect(() => {
    const processData = () => {
      const questionResponses = {};

      // List of explicitly allowed MQ numbers
      const allowedMQNumbers = [
        "MQ0017",
        "MQ0048",
        "MQ0051",
        "MQ0050",
        "MQ0095",
        "MQ0016",
        "MQ0059",
        "MQ0104",
        "MQ0173",
        "MQ0054",
        "MQ0190",
        "MQ0141",
        "MQ0343",
        "MQ0088",
        "MQ0090",
        "MQ0091",
        "MQ0086",
        "MQ0034",
        "MQ0041",
        "MQ0194",
        "MQ0321",
        "MQ0058",
        "MQ0053",
        "MQ0171",
        "MQ0103",
        "MQ0056",
        "MQ0057",
        "MQ0038",
        "MQ0071",
        "MQ0202",
        "MQ0027",
        "MQ0023",
        "MQ0024",
        "MQ0026",
        "MQ0064",
        "MQ0043",
        "MQ0062",
        "MQ0340",
        "MQ0341",
        "MQ0094",
        "MQ0021",
        "MQ0080",
        "MQ0302",
        "MQ0029",
        "MQ0012",
        "MQ0072",
        "MQ0331",
        "MQ0311",
        "MQ0312",
        "MQ0320",
        "MQ0046",
        "MQ0100",
        "MQ0044",
        "MQ0047",
      ];

      const filteredJsonData = jsonData.filter((item) => {
        return Object.keys(filterCriteria).every((key) => {
          if (!filterCriteria[key].length) return true; // No filter applied for this key
          return filterCriteria[key].includes(item[key]);
        });
      });

      filteredJsonData.forEach((item) => {
        Object.keys(item)
          .filter((key) => allowedMQNumbers.includes(key)) // Use the whitelist
          .forEach((questionCode) => {
            if (!questionResponses[questionCode]) {
              questionResponses[questionCode] = [];
            }
            questionResponses[questionCode].push(item[questionCode]);
          });
      });

      const result = Object.entries(questionResponses).map(
        ([questionCode, responses]) => {
          const questionInfo = questionData.find(
            (q) => q.q_num === questionCode
          );
          const total = responses.reduce((sum, value) => sum + (value || 0), 0);
          const average = total / responses.length || 0;

          return {
            code: questionCode,
            question: questionInfo?.Question_Text || "Unknown Question",
            area: questionInfo?.engagement_area || "Unknown Area",
            average,
          };
        }
      );

      setData(result);
    };

    processData();
  }, [filterCriteria]);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const toggleFilterSidebar = () =>
    setIsFilterSidebarOpen(!isFilterSidebarOpen);

  const toggleSortOrder = () =>
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));

  const handleSearch = (event) => setSearchTerm(event.target.value);

  // Function to download data as CSV
  const downloadCSV = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      [
        ["Code", "Question", "Area", "Average"],
        ...data.map((item) => [
          `"${item.code}"`,
          `"${item.question}"`,
          `"${item.area}"`,
          `"${item.average.toFixed(2)}"`,
        ]),
      ]
        .map((row) => row.join(",")) // Use semicolons as delimiters
        .join("\n");

    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "engagement_questions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredData = data
    .filter((item) =>
      item.question.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) =>
      sortOrder === "asc" ? a.average - b.average : b.average - a.average
    );

  return (
    <div className="D09_Engagement_Questions_Sorted-dashboard-container">
      <Header
        title="Engagement Questions Table"
        toggleSidebar={toggleSidebar}
        toggleFilterSidebar={toggleFilterSidebar}
        refreshPage={refreshPage}
      />
      <NavigationSidebar isOpen={isSidebarOpen} />
      <FilterSidebar
        isOpen={isFilterSidebarOpen}
        filterOptions={filterOptions}
        setFilterCriteria={setFilterCriteria}
      />
      <div className="D09_Engagement_Questions_Sorted-main-content">
        <div className="search-bar-container" style={{ marginBottom: "16px" }}>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            className="p-2 border rounded search-bar"
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "16px",
            }}
          />
          <button onClick={downloadCSV} className="download-button">
            Download CSV
          </button>
        </div>
        <div className="D09_Engagement_Questions_Sorted-scrollable-table">
          <EngagementTable
            data={filteredData}
            sortOrder={sortOrder}
            toggleSortOrder={toggleSortOrder}
            searchTerm={searchTerm}
          />
        </div>
      </div>
    </div>
  );
};

export default CCISortedEngagementQuestions;